/** @jsxImportSource @emotion/react */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import classnames from 'classnames';
import { InfoCircle } from '@nike/nike-design-system-icons';
import { css } from '@emotion/react';

import { Button, CountrySelect, RenderInBody, Spinner } from '../components';
import Select from '~COMPONENTS/formInputs/Select';
import Input from '~COMPONENTS/formInputs/Input';
import DeleteAddress from './deleteAddress';
import { TranslatedCopy } from '../intl/TranslatedCopy';
import ToolTip from '../components/ToolTip';
import {
  required,
  maxLength4,
  maxLength6,
  maxLength25,
  maxLength35,
  minLength6,
  minLength3,
  minLength4,
  noSelectSpecialChars,
  noSpecialCharsAddress,
  validateZipCode,
  validateJapanZipCode,
  stateRequired,
  phoneFormat
} from '~APP_UTIL/formValidations';
import { validatePostalCode } from '../util/postalCodeValidations';
import countries from '../util/countriesSAP';
import cities from '../util/cities';
import {
  getCNCityTranslationAccessor,
  getCNProvinceTranslationAccessor,
  getCNTranslationAccessor
} from '../util/languageTranslations';

import {
  closeSidebar,
  deleteAddressAndUpdateState,
  requestAddresses,
  createAddress,
  saveAddress,
  googleValidateAddress
} from './actions';
import { sapAddresses } from './selectors';

import { getUserData, getUserAccount, accountDetails } from '../user/selectors';
import { openModal } from '../components/modal/actions';

import { addressTypes } from '../types/address';
import { userAccountTypes } from '../types/user';

import {
  getIsGreaterChinaSalesOrg,
  getIsChinaSalesOrg,
  getIsHongKongOrTaiwanSalesOrg,
  getSalesOrg,
  getIsAustraliaSalesOrg,
  getIsMANASalesOrg,
  getIsJapanSalesOrg,
  getIsEUSalesOrg
} from '../gamedayAccount/selectors';

import getHongKongOrTaiwanStateOptions from '../util/getHongKongOrTaiwanStateOptions';
import getHongKongOrTaiwanPostalCodeMaxLength from '../util/getHongKongOrTaiwanPostalCodeMaxLength';

export class AddressBookForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      normalizedCountries: [],
      normalizedStates: [],
      normalizedCities: [],
      addressErrors: []
    };
  }

  componentDidMount() {
    const { sapAddresses, isGreaterChinaSalesOrg } = this.props;
    if (sapAddresses) {
      this.initializeCountries();
    }
    if (isGreaterChinaSalesOrg) {
      this.initializeCities();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { editAddress, account, closeSidebar, reset, sapAddresses, isGreaterChinaSalesOrg } = this.props;
    const { normalizedCountries } = this.state;

    if (
      (!editAddress && prevProps.editAddress !== editAddress) ||
      (account &&
        account.number &&
        prevProps.account &&
        prevProps.account.number &&
        account.number !== prevProps.account.number)
    ) {
      this.resetCountry();
      reset();
      closeSidebar();
    }

    if (prevProps.sapAddresses !== sapAddresses) {
      this.initializeCountries();
    }

    if (!isGreaterChinaSalesOrg && prevState.normalizedCountries !== normalizedCountries) {
      this.initializeAutoComplete();
    }
  }

  setRef = node => (this.textInput = node);

  setNormalizedCountries = normalizedCountries => this.setState({ normalizedCountries });

  setNormalizedStates = normalizedStates => this.setState({ normalizedStates });

  setNormalizedCities = normalizedCities => this.setState({ normalizedCities });

  autocomplete = null;

  // Overriding Chromes autofill.
  observerHack = new MutationObserver(() => {
    this.observerHack.disconnect();
    const name = document.getElementById('addressName');
    const address = document.getElementById('address');

    if (name && address) {
      name.setAttribute('autocomplete', 'name0');
      address.setAttribute('autocomplete', 'address1');
    }
  });

  initializeAutoComplete = () => {
    const { normalizedCountries } = this.state;
    const canObserveMutation = window && 'MutationObserver' in window;
    if (canObserveMutation) {
      this.observerHack.observe(document.getElementById('address'), {
        attributes: true,
        attributeFilter: ['autocomplete']
      });
      // Create the autocomplete object, restricting the search to geographical location types.
      this.autocomplete = new window.google.maps.places.Autocomplete(document.getElementById('address'), {
        types: ['geocode']
      });
      this.autocomplete.setComponentRestrictions({ country: normalizedCountries.map(country => country.value) });
      // When the user selects an address from the dropdown, populate the address fields in the form.
      this.autocomplete.addListener('place_changed', this.fillInAddress);
    }
  };

  initializeCountries = () => {
    const { sapAddresses, isGreaterChinaSalesOrg, userData } = this.props;
    const normalizedCountries = [];
    const normalizedStates = [];

    countries.forEach(country => {
      if (sapAddresses?.length && country.code2 === sapAddresses[0].addresses[0].country) {
        const isCN = isGreaterChinaSalesOrg && country.code2 === 'CN';
        const countryName = isCN ? getCNTranslationAccessor(userData?.locale) : 'name';
        normalizedCountries.push({ value: country.code2, label: country[countryName] });

        const stateName = isCN ? getCNProvinceTranslationAccessor(userData?.locale) : 'name';
        normalizedStates.push(
          country.states
            .map(state => ({ value: state.code, label: state[stateName] }))
            .sort((a, b) => {
              const labelA = a.label.split(' ').join().toLowerCase();
              const labelB = b.label.split(' ').join().toLowerCase();
              if (labelA < labelB) return -1;
              if (labelA > labelB) return 1;
              return 0;
            })
        );
      } else if (!sapAddresses) {
        normalizedCountries.push({ value: country.code2, label: country.name });
      }
    });
    this.setNormalizedCountries(normalizedCountries);
    this.setNormalizedStates(normalizedStates);
  };

  initializeCities = () => {
    const { isGreaterChinaSalesOrg, userData } = this.props;
    if (isGreaterChinaSalesOrg) {
      const cityName = getCNCityTranslationAccessor(userData?.locale);
      const normalizedCities = cities
        .map(city => ({ value: city.normalizedCityName, label: city[cityName] }))
        .sort((a, b) => {
          const labelA = a.label.split(' ').join().toLowerCase();
          const labelB = b.label.split(' ').join().toLowerCase();
          if (labelA < labelB) return -1;
          if (labelA > labelB) return 1;
          return 0;
        });
      this.setNormalizedCities(normalizedCities);
    }
  };

  fillInAddress = () => {
    const { change } = this.props;
    const { normalizedCountries } = this.state;

    // Get address details from the autocomplete object.
    const newAddress = this.autocomplete.getPlace().address_components.reduce(
      (addressObj, field) => ({
        ...addressObj,
        [field.types[0]]: this.formatAddressField(field)
      }),
      {}
    );

    const streetWithNumber = newAddress.street_number
      ? `${newAddress.street_number} ${newAddress.route}`
      : newAddress.route;
    const stateIsRequired =
      normalizedCountries.length > 0 &&
      (normalizedCountries[0].value === 'US' || normalizedCountries[0].value === 'CA');

    // TODO: Review ruleset for regions without state and no-abbr field options before removing this check
    if (stateIsRequired) change('state', newAddress.administrative_area_level_1);

    change('city', newAddress.locality || newAddress.sublocality_level_1 || newAddress.neighborhood);
    change('address', streetWithNumber);
    change('country', newAddress.country);
    change('zipCode', newAddress.postal_code);
  };

  formatAddressField = field => {
    switch (field.types[0]) {
      case 'administrative_area_level_1':
        return {
          value: field.short_name,
          label: field.long_name
        };
      default:
        return field.short_name;
    }
  };

  resetCountry = () => {
    const { change } = this.props;
    return change('country', '');
  };

  toggleView = () => this.setState(state => ({ showListView: !state.showListView }));

  refreshAddressList = () => {
    const { fetchAddresses, closeSidebar } = this.props;
    closeSidebar();
    fetchAddresses();
  };

  handleDelete = addressId => {
    const { deleteAddress, setSearchTerm, userData } = this.props;
    deleteAddress({ username: userData.id, id: addressId }).then(() => {
      setSearchTerm('');
      this.refreshAddressList();
    });
  };

  handleAddressValidation = async savedAddress => {
    const { isMANASalesOrg, scrollRef } = this.props;

    if (!isMANASalesOrg) return savedAddress;

    this.setState({ addressErrors: [] });

    const { address, city, state, zipCode } = savedAddress;

    const enteredAddress = {
      addressLines: [address],
      administrativeArea: state,
      locality: city,
      regionCode: 'US',
      postalCode: zipCode
    };

    const response = await googleValidateAddress(enteredAddress);

    const addressErrors = [];
    if (response.result.verdict.hasUnconfirmedComponents) {
      response.result.address.unconfirmedComponentTypes?.forEach(addressComponent => {
        if (addressComponent === 'locality' || addressComponent === 'neighborhood') {
          addressErrors.push('City');
        } else if (addressComponent === 'administrative_area_level_1') {
          addressErrors.push('State');
        } else if (addressComponent === 'postal_code') {
          addressErrors.push('Zip Code');
        }
      });
    }

    if (addressErrors.length) {
      this.setState({ addressErrors });
      scrollRef.current.scrollTo(0, 0);
      throw new SubmissionError(`Invalid address components: ${addressErrors.join(', ')}`);
    }

    if (
      response.result.verdict.hasReplacedComponents &&
      (response.result.address.postalAddress.locality !== enteredAddress.locality ||
        response.result.address.postalAddress.postalCode?.substring(0, 5) !==
          enteredAddress.postalCode?.substring(0, 5))
    ) {
      this.setState({ addressErrors: ['City does not match Zip Code'] });
      scrollRef.current.scrollTo(0, 0);
      throw new SubmissionError('City does not match Zip Code');
    }

    const updatedSavedAddress = { ...savedAddress };
    if (response.result.verdict.hasInferredComponents || response.result.verdict.hasReplacedComponents) {
      updatedSavedAddress.city = response.result.address.postalAddress.locality;
      updatedSavedAddress.state = response.result.address.postalAddress.administrativeArea;
      updatedSavedAddress.zipCode = response.result.address.postalAddress.postalCode;
    }

    return updatedSavedAddress;
  };

  submitForm = address => {
    const {
      closeSidebar,
      editAddress,
      handleAddressClick,
      isCheckout,
      requestAddresses,
      reset,
      createAddress,
      setSearchTerm,
      saveAddress,
      userData
    } = this.props;
    const submitFn = editAddress ? saveAddress : createAddress;
    // handle object being passed on custom addresses not recognized by address autofill
    const savedAddress = {
      ...address,
      // eslint-disable-next-line no-nested-ternary
      state: typeof address.state === 'string' ? address.state : address.state ? address.state.value : '',
      city: typeof address.city === 'string' ? address.city : address.city.value,
      country: typeof address.country === 'string' ? address.country : address.country.value
    };

    return this.handleAddressValidation(savedAddress).then(updatedSavedAddress =>
      submitFn({ username: userData.email, address: updatedSavedAddress }).then(async () => {
        reset();
        setSearchTerm('');
        closeSidebar();
        requestAddresses().then(({ payload }) => {
          if (isCheckout) {
            const normalizedAddress = {
              ...updatedSavedAddress,
              name: updatedSavedAddress.addressName,
              addressLine1: updatedSavedAddress.address,
              addressLine2: updatedSavedAddress.address2,
              cityStateZip: `${updatedSavedAddress.city}, ${updatedSavedAddress.state} ${updatedSavedAddress.zipCode}`,
              zipCode: updatedSavedAddress.zipCode
            };
            // If user adds a new address in checkout, we need to request the ID from the DB before passing it along
            const currOneTimeId = payload.filter(
              addr => addr.name === normalizedAddress.name && addr.addressLine1 === normalizedAddress.addressLine1
            );

            if (currOneTimeId) {
              handleAddressClick({ address: { ...normalizedAddress, id: currOneTimeId[0].id }, isNewAddress: true });
            }
          }
        });
      })
    );
  };

  handleSaveAddressClick = e => {
    const { handleSubmit } = this.props;
    return handleSubmit(this.submitForm)(e);
  };

  submitButton = () => {
    const { addressBookForm, editAddress, isOpen, invalid, submitting, pristine, isCheckout } = this.props;
    let hasNotUpdated = true;

    function formUpdates(original, updated) {
      const originalValues = Object.values(original).filter(val => !!val);
      const updatedValues = Object.values(updated).filter(val => !!val);
      return updatedValues.every(updatedElement => originalValues.includes(updatedElement));
    }

    if (addressBookForm && addressBookForm.values && editAddress) {
      const { city, state, zipCode } = addressBookForm.values;
      const formValues = {
        cityStateZip: `${city}, ${state} ${zipCode}`,
        isLocked: false,
        ...addressBookForm.values
      };

      hasNotUpdated = formUpdates(editAddress, formValues);
    }

    const submitButtonText = isCheckout ? (
      <TranslatedCopy id='addressBook.form.edit.checkout.submit' />
    ) : (
      <TranslatedCopy id='addressBook.form.edit.submit' />
    );
    return (
      <div className={classnames('sidebar__submit prl5-sm pb5-sm', { isOpen: isOpen })}>
        <Button
          extraClassName='u-full-width mt5-sm'
          onClick={this.handleSaveAddressClick}
          disabled={invalid || submitting || (editAddress && hasNotUpdated) || pristine}
          data-desc='addressBookForm__button_submit'
        >
          {submitting ? <Spinner className='relative-container p3-sm' /> : submitButtonText}
        </Button>
      </div>
    );
  };

  handleEditAddressLoad = () => {
    const { editAddress } = this.props;
    if (editAddress) {
      const {
        editAddress: {
          id,
          name,
          addressLine1,
          addressLine2,
          attention,
          city,
          state,
          country,
          zipCode,
          phoneNumber,
          shopSuiteFloor,
          districtName
        },
        change
      } = this.props;

      const countryCode = country === 'USA' ? 'US' : country;
      const countryData = countries.find(c => c.code2 === countryCode);
      const stateData = countryData?.states.find(s => s.code === state);

      change('addressName', name);
      change('attention', attention);
      change('address', addressLine1);
      change('address2', addressLine2);
      change('city', city);
      change('state', stateData ? { label: stateData.name, value: stateData.code } : state);
      change('country', countryCode);
      change('zipCode', zipCode);
      change('id', id);
      change('phoneNumber', phoneNumber);
      change('shopSuiteFloor', shopSuiteFloor);
      change('districtName', districtName);
    }
  };

  render() {
    const {
      editAddress,
      isCheckout,
      openModal,
      submitting,
      isGreaterChinaSalesOrg,
      isChinaSalesOrg,
      isHongKongOrTaiwanSalesOrg,
      isAustraliaSalesOrg,
      isMANASalesOrg,
      isJapanSalesOrg,
      isEUSalesOrg,
      salesOrg,
      change
    } = this.props;
    const {
      normalizedCountries,
      normalizedStates,
      normalizedCities,
      addressErrors
    } = this.state; /*
    // TODO: Add back in once we get clarification on default State requirements for SAP
    const stateIsRequired =
      normalizedCountries.length > 0 &&
      (normalizedCountries[0].value === 'US' || normalizedCountries[0].value === 'CA');
    */

    return (
      // eslint-disable-next-line react/no-unknown-property
      <div className='pb12-sm' onLoad={this.handleEditAddressLoad}>
        {addressErrors.length > 0 && (
          <div css={{ color: 'red', paddingBottom: '5px' }}>
            Invalid address component(s): {addressErrors.join(', ')}
          </div>
        )}
        {isCheckout ? this.submitButton() : <RenderInBody>{this.submitButton()}</RenderInBody>}
        <Field
          name='addressName'
          component={Input}
          label={new TranslatedCopy({ id: 'global.form.organization' }).toString()}
          validate={[required, maxLength25, noSpecialCharsAddress]}
          required
        />
        {/* recipient for non-GC only */}
        {!isGreaterChinaSalesOrg && (
          <Field
            name='attention'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.recipient' }).toString()}
            validate={[maxLength35, noSpecialCharsAddress]}
          />
        )}
        {/* shop / floor / suite for GC only */}
        {isGreaterChinaSalesOrg && (
          <Field
            name='shopSuiteFloor'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.suite' }).toString()}
            validate={[maxLength35, noSelectSpecialChars]}
          />
        )}
        <Field
          name='address'
          component={Input}
          label={new TranslatedCopy({ id: 'global.form.address' }).toString()}
          validate={[required, maxLength35, noSpecialCharsAddress]}
          placeholder={{ id: 'global.form.address.placeholder' }}
          ref={this.setRef}
          required
        />
        {/* Address Line 2 for AUS + EMEA + Japan only */}
        {(isAustraliaSalesOrg || isEUSalesOrg || isJapanSalesOrg) && (
          <Field
            name='address2'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.address2' }).toString()}
            validate={[maxLength35, noSpecialCharsAddress]}
            placeholder={{ id: 'global.form.address2.placeholder' }}
          />
        )}
        {/* district for GC only */}
        {isGreaterChinaSalesOrg && (
          <div css={{ display: 'flex' }}>
            <div css={{ flex: 1 }}>
              <Field
                name='districtName'
                component={Input}
                label={new TranslatedCopy({ id: 'global.form.district' }).toString()}
                validate={[required, maxLength25, noSelectSpecialChars]}
                required
              />
            </div>
            <div
              css={css`
                .tooltip {
                  top: 14px;
                }
              `}
            >
              <ToolTip
                position='left'
                className='districtTooltip'
                title={new TranslatedCopy({ id: 'global.form.district.tooltip' }).toString()}
                css={css`
                  .tooltip__content {
                    width: 160px;
                    white-space: break-spaces;
                    font-weight: 600;
                    p {
                      color: white;
                    }
                  }
                `}
              >
                <InfoCircle />
              </ToolTip>
            </div>
          </div>
        )}
        {isChinaSalesOrg ? (
          <Field
            name='city'
            component={Select}
            options={normalizedCities}
            label={new TranslatedCopy({ id: 'global.form.city' }).toString()}
            validate={[required]}
            placeholder={{ id: 'global.form.city.placeholder' }}
            extraClassName='body-3'
            onChange={(e, city) => {
              if (city) {
                const mappedPostalCode = cities.find(c => c.normalizedCityName === city.value)?.postalCode;
                if (mappedPostalCode) change('zipCode', mappedPostalCode);
              }
            }}
            clearable
            required
            forceDropdown
          />
        ) : (
          <Field
            name='city'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.city' }).toString()}
            validate={[required, maxLength35, noSelectSpecialChars]}
            placeholder={{ id: 'global.form.city.placeholder' }}
            required
          />
        )}
        <Field
          name='state'
          component={Select}
          options={isHongKongOrTaiwanSalesOrg ? getHongKongOrTaiwanStateOptions(salesOrg) : normalizedStates[0] || []}
          label={new TranslatedCopy({
            id: isJapanSalesOrg ? 'global.form.prefecture' : 'global.form.state'
          }).toString()}
          validate={[stateRequired]}
          // TODO: Add back in with above temp-removals - {stateIsRequired ? [required] : []}
          extraClassName='body-3'
          clearable
          required
          forceDropdown
        />
        {/* postal code here for GC (below for other accounts) */}
        {isGreaterChinaSalesOrg && (
          <Field
            name='zipCode'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.zip' }).toString()}
            validate={[
              required,
              ...(isHongKongOrTaiwanSalesOrg ? getHongKongOrTaiwanPostalCodeMaxLength(salesOrg) : [maxLength6]),
              ...(isHongKongOrTaiwanSalesOrg ? [minLength3] : [minLength6]),
              noSelectSpecialChars
            ]}
            placeholder={{ id: 'global.form.zip.placeholder' }}
            autoComplete='postal-code'
            required
          />
        )}
        {/* country -> country / region for GC */}
        <Field
          name='country'
          component={CountrySelect}
          label={
            isGreaterChinaSalesOrg
              ? new TranslatedCopy({ id: 'global.form.country.region' }).toString()
              : new TranslatedCopy({ id: 'global.country' }).toString()
          }
          validate={[required]}
          options={normalizedCountries}
          placeholder={{ id: 'global.form.country.placeholder' }}
          extraClassName='body-3'
          autoComplete='country'
          clearable={false}
          initialValue={normalizedCountries[0]}
          required
          forceDropdown
        />
        {/* postal code here for all accounts but GC */}
        {!isGreaterChinaSalesOrg && (
          <Field
            name='zipCode'
            component={Input}
            label={new TranslatedCopy({ id: 'global.form.zip' }).toString()}
            validate={[
              required,
              noSelectSpecialChars,
              ...(isAustraliaSalesOrg ? [maxLength4, minLength4] : []),
              ...(isMANASalesOrg ? [validateZipCode] : []),
              ...(isJapanSalesOrg ? [validateJapanZipCode] : [])
            ]}
            placeholder={{ id: 'global.form.zip.placeholder' }}
            onChange={(e, zipCode) => {
              // add a dash into zipCode if user types a 6th digit
              if (isMANASalesOrg && zipCode?.length === 6 && /^.{5}(\d)/.test(zipCode)) {
                const formattedZipCode = `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`;
                change('zipCode', formattedZipCode);
                e.target.value = formattedZipCode;
              } else if (isJapanSalesOrg && zipCode?.length === 4 && /^.{3}(\d)/.test(zipCode)) {
                const formattedZipCode = `${zipCode.slice(0, 3)}-${zipCode.slice(3)}`;
                change('zipCode', formattedZipCode);
                e.target.value = formattedZipCode;
              }
            }}
            required
          />
        )}
        <Field
          name='phoneNumber'
          component={Input}
          label={new TranslatedCopy({ id: 'global.form.phone' }).toString()}
          placeholder={{ id: 'global.form.phone' }}
          validate={[
            ...(isGreaterChinaSalesOrg || isJapanSalesOrg ? [required] : []),
            phoneFormat
          ]}
          {...((isGreaterChinaSalesOrg || isJapanSalesOrg) && { required: true })}
        />
        {editAddress && (
          <div className='mt2-sm'>
            <DeleteAddress
              handleDelete={this.handleDelete}
              openModal={openModal}
              addressId={editAddress.id}
              submitting={submitting}
            />
          </div>
        )}
      </div>
    );
  }
}

AddressBookForm.propTypes = {
  account: userAccountTypes,
  addressBookForm: PropTypes.any.isRequired,
  change: PropTypes.func.isRequired,
  closeSidebar: PropTypes.func.isRequired,
  deleteAddress: PropTypes.func.isRequired,
  editAddress: PropTypes.any,
  fetchAddresses: PropTypes.func.isRequired,
  handleAddressClick: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  isCheckout: PropTypes.bool,
  isOpen: PropTypes.bool,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  isHongKongOrTaiwanSalesOrg: PropTypes.bool.isRequired,
  isChinaSalesOrg: PropTypes.bool.isRequired,
  isAustraliaSalesOrg: PropTypes.bool.isRequired,
  isMANASalesOrg: PropTypes.bool.isRequired,
  isJapanSalesOrg: PropTypes.bool.isRequired,
  isEUSalesOrg: PropTypes.bool.isRequired,
  salesOrg: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  requestAddresses: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  sapAddresses: PropTypes.arrayOf(PropTypes.shape(addressTypes)),
  createAddress: PropTypes.func.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  saveAddress: PropTypes.func.isRequired,
  userData: PropTypes.any,
  scrollRef: PropTypes.any.isRequired
};

AddressBookForm.defaultProps = {
  account: null,
  editAddress: null,
  isCheckout: false,
  sapAddresses: null,
  userData: null,
  isOpen: false
};

const mapStateToProps = state => ({
  account: getUserAccount(state),
  addressBookForm: state.form.addressBookForm,
  sapAddresses: sapAddresses(state),
  userData: getUserData(state),
  accountDetails: accountDetails(state),
  isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state),
  isChinaSalesOrg: getIsChinaSalesOrg(state),
  isHongKongOrTaiwanSalesOrg: getIsHongKongOrTaiwanSalesOrg(state),
  isAustraliaSalesOrg: getIsAustraliaSalesOrg(state),
  isMANASalesOrg: getIsMANASalesOrg(state),
  isJapanSalesOrg: getIsJapanSalesOrg(state),
  isEUSalesOrg: getIsEUSalesOrg(state),
  salesOrg: getSalesOrg(state)
});

const connector = connect(mapStateToProps, {
  closeSidebar,
  deleteAddress: deleteAddressAndUpdateState,
  openModal,
  requestAddresses,
  createAddress,
  saveAddress
});

export default reduxForm({
  form: 'addressBookForm',
  validate: validatePostalCode, // need form-level validation since checks country and postalCode together
  touchOnChange: true
})(connector(AddressBookForm));
